import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analyticsApi, serviceApi, bookingApi } from '../services/api';

interface BookingAnalytics {
  today: number;
  week: number;
  month: number;
  statusDistribution: {
    PENDING: number;
    CONFIRMED: number;
    COMPLETED: number;
    CANCELLED: number;
  };
}

interface RevenueAnalytics {
  today: number;
  month: number;
  trend: Array<{
    date: string;
    revenue: number;
  }>;
}

interface CustomerAnalytics {
  total: number;
  newThisMonth: number;
  activeThisMonth: number;
}

interface RecentBooking {
  id: string;
  customer: { name: string };
  service: { name: string };
  date: string;
  status: string;
}

interface DashboardState {
  bookingStats: BookingAnalytics | null;
  revenueStats: RevenueAnalytics | null;
  customerStats: CustomerAnalytics | null;
  serviceCount: number;
  recentBookings: RecentBooking[];
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  fetchDashboardData: (storeId: string, forceRefresh?: boolean) => Promise<void>; // 需要 storeId 參數
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      bookingStats: null,
      revenueStats: null,
      customerStats: null,
      serviceCount: 0,
      recentBookings: [],
      isLoading: false,
      error: null,
      lastFetchTime: null,

      fetchDashboardData: async (storeId: string, forceRefresh = false) => {
        const state = get();
        const CACHE_DURATION = 2 * 60 * 1000; // 2 分鐘快取

        // 強制刷新時直接獲取
        if (forceRefresh) {
          set({ isLoading: true, error: null });
          try {
            const [bookingRes, revenueRes, customerRes, servicesRes, bookingsRes] = await Promise.all([
              analyticsApi.bookings(), // 後端從認證自動獲取
              analyticsApi.revenue(), // 後端從認證自動獲取
              analyticsApi.customers(), // 後端從認證自動獲取
              serviceApi.getByStore(storeId), // 公開 API，需要 storeId
              bookingApi.list({ storeId, limit: 5 }), // 需要 storeId
            ]);

            set({
              bookingStats: bookingRes.data,
              revenueStats: revenueRes.data,
              customerStats: customerRes.data,
              serviceCount: servicesRes.data.length,
              recentBookings: bookingsRes.data.slice(0, 5),
              isLoading: false,
              lastFetchTime: Date.now(),
            });
          } catch (error: any) {
            console.error('Failed to fetch dashboard data:', error);
            set({ error: '無法載入儀表板數據', isLoading: false });
          }
          return;
        }

        // 檢查快取是否仍然有效（2 分鐘內）
        const now = Date.now();
        const isCacheValid = state.bookingStats &&
                            state.lastFetchTime &&
                            (now - state.lastFetchTime) < CACHE_DURATION;

        if (isCacheValid) {
          // 快取仍然有效，不需要重新載入
          return;
        }

        // 如果有舊數據，在背景更新（不顯示 loading，避免閃爍）
        // 如果沒有舊數據，顯示 loading
        const shouldShowLoading = !state.bookingStats;

        if (shouldShowLoading) {
          set({ isLoading: true, error: null });
        }

        try {
          const [bookingRes, revenueRes, customerRes, servicesRes, bookingsRes] = await Promise.all([
            analyticsApi.bookings(), // 後端從認證自動獲取
            analyticsApi.revenue(), // 後端從認證自動獲取
            analyticsApi.customers(), // 後端從認證自動獲取
            serviceApi.getByStore(storeId), // 公開 API，需要 storeId
            bookingApi.list({ storeId, limit: 5 }), // 需要 storeId
          ]);

          set({
            bookingStats: bookingRes.data,
            revenueStats: revenueRes.data,
            customerStats: customerRes.data,
            serviceCount: servicesRes.data.length,
            recentBookings: bookingsRes.data.slice(0, 5),
            isLoading: false,
            lastFetchTime: Date.now(),
          });
        } catch (error: any) {
          console.error('Failed to fetch dashboard data:', error);
          set({ error: '無法載入儀表板數據', isLoading: false });
        }
      },
    }),
    {
      name: 'dashboard-storage',
      partialize: (state) => ({
        bookingStats: state.bookingStats,
        revenueStats: state.revenueStats,
        customerStats: state.customerStats,
        serviceCount: state.serviceCount,
        recentBookings: state.recentBookings,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);
